import log from 'loglevel';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { fetchSite } from '../sitesApi';
import { Site } from '../siteTypes';

interface SiteState {
  site: Site | null;
  load: (id: string) => Promise<void>;
}

export const SiteContext = createContext<SiteState>({
  site: null,
  load: async (id: string) => {},
});

export function SiteProvider(props: Readonly<{ children: React.ReactNode }>) {
  const [site, setSite] = useState<Site | null>(null);

  const load = useCallback(async (id: string) => {
    // Fetch site.
    const site = await fetchSite(id);

    if (!site) {
      log.error('Forbidden: Access is denied');
      return;
    }

    // Check site origins allowed with referrer.
    const referrer = window.document.referrer;

    if (site.origin !== referrer) {
      log.error('Forbidden: Access is denied');
      return;
    }

    // Save site.
    setSite(site);

    log.debug('Loaded site');
  }, []);

  const value = useMemo(() => ({ site, load }), [site, load]);

  return <SiteContext.Provider value={value} {...props} />;
}

export function useSite() {
  const context = useContext(SiteContext);
  if (context === undefined) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
}
